import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { API_CONFIG } from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import Modal from '../components/Modal';
import AssignGroupModal from '../components/AssignGroupModal';

const ASSESSMENT_HISTORY_MAX_VISIBLE = 4;

// Types for API response
interface UserDetail {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone?: string;
  profile?: any;
  plan: string;
  hasSubscription: boolean;
  subscriptionId?: string;
  startDate?: string;
  endDate?: string;
  status: string;
  createdAt: string;
  lastLogin: string;
  isActive: boolean;
  role: string;
}

interface SubscriptionDetail {
  id: string;
  plan: string;
  price: number;
  billingCycle: string;
  status: string;
  nextPayment?: string;
  paymentMethod?: any;
  totalBilled: number;
}

interface HistoryItem {
  type: string;
  description: string;
  date: string;
  icon: string;
}

interface UserDetailResponse {
  user: UserDetail;
  subscription?: SubscriptionDetail;
  history: HistoryItem[];
}

interface SOWBAttribute {
  key: string;
  label: string;
  icon: string;
  score: number;
  percent: number;
  level: string;
}

interface SOWBOverall {
  total0to40: number;
  average0to4: number;
  percent0to100: number;
  overallLevel: string;
  createdAt: string | null;
}

interface SOWBHistoryItem {
  id: string;
  createdAt: string;
  overallScore: number;
  overallLevel: string;
  scores: any;
  results: any;
}

interface SOWBData {
  attributes: SOWBAttribute[];
  overall: SOWBOverall | null;
  latest: any;
  history: SOWBHistoryItem[];
}

const ConsultantUsersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  const [userData, setUserData] = useState<UserDetail | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionDetail | null>(null);
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [sowbData, setSowbData] = useState<SOWBData | null>(null);
  const [sowbAccessError, setSowbAccessError] = useState<string | null>(null);
  const [sowbRefreshing, setSowbRefreshing] = useState(false);
  const [showAllHistoryModal, setShowAllHistoryModal] = useState(false);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const isEnterpriseUserPage = location.pathname.includes('/panel/enterprise/user');
  const isConsultant = (userData?.role || '').toString().toLowerCase() === 'consultant';

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!id) {
        setError('User ID not provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user details');
        }

        const fullResponse = await response.json();
        const data: UserDetailResponse = fullResponse.data;

        setUserData(data.user);
        setSubscriptionData(data.subscription || null);
        setHistoryData(data.history || []);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load user details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  const fetchSOWBData = React.useCallback(async (isRefresh = false) => {
    if (!id) return;
    if (isRefresh) setSowbRefreshing(true);

    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING_CONSULTANT}/${id}?limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        if (response.status === 403) {
          setSowbAccessError('You can only view Star of Well-Being data for approved members of your groups.');
        } else {
          console.warn('Failed to fetch SOWB data:', response.status);
        }
        return;
      }

      const fullResponse = await response.json();
      const data = fullResponse.data;
      setSowbData(data);
      setSowbAccessError(null);
    } catch (err) {
      console.warn('Error fetching SOWB data:', err);
    } finally {
      if (isRefresh) setSowbRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSOWBData();
  }, [fetchSOWBData]);

  const handleStatusToggle = async () => {
    if (!id || !userData || statusUpdating) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const nextActive = !userData.isActive;
    setStatusError(null);
    setStatusUpdating(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS}/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: nextActive })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update status');
      }
      setUserData((prev) => (prev ? { ...prev, isActive: result.data?.user?.isActive ?? nextActive } : null));
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`consultant-users ${isDarkMode ? 'consultant-users--dark' : ''}`}>
        <div className="consultant-users__container">
          <div className="consultant-users__loading">
            <div className="consultant-users__spinner"></div>
            <p>Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !userData) {
    return (
      <div className={`consultant-users ${isDarkMode ? 'consultant-users--dark' : ''}`}>
        <div className="consultant-users__container">
          <div className="consultant-users__error">
            <span className="material-symbols-outlined consultant-users__error-icon">error</span>
            <h2 className="consultant-users__error-title">Error Loading User</h2>
            <p className="consultant-users__error-message">{error || 'User not found'}</p>
            <button
              className="consultant-users__error-back-btn"
              onClick={() => navigate('/panel/consultant/users')}
            >
              Back to Users List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`consultant-users ${isDarkMode ? 'consultant-users--dark' : ''}`}>
      <div className="consultant-users__container">
        {/* Header */}
        <div className="consultant-users__header">
          <div className="consultant-users__breadcrumb">
            <button
              className="consultant-users__breadcrumb-link"
              onClick={() => navigate('/panel/consultant')}
            >
              <span className="consultant-users__breadcrumb-item">Consultant Dashboard</span>
            </button>
            <span className="consultant-users__breadcrumb-separator">
              <span className="material-symbols-outlined consultant-users__breadcrumb-icon">chevron_right</span>
            </span>
            <span className="consultant-users__breadcrumb-item consultant-users__breadcrumb-item--active">User Details</span>
          </div>
          <h1 className="consultant-users__title">User Profile</h1>
        </div>

        {/* User Profile Card */}
        <div className="consultant-users__profile-card">
          <div className="consultant-users__status-row">
            <button
              type="button"
              className={`consultant-users__status-toggle consultant-users__status-toggle--${userData.isActive ? 'active' : 'inactive'}`}
              onClick={handleStatusToggle}
              disabled={statusUpdating}
              aria-label={userData.isActive ? 'Set user inactive' : 'Set user active'}
              aria-pressed={userData.isActive}
            >
              <span className="consultant-users__status-dot"></span>
              {statusUpdating ? 'Updating…' : (userData.isActive ? 'Active' : 'Inactive')}
            </button>
            {statusError && (
              <span className="consultant-users__status-error" role="alert">{statusError}</span>
            )}
          </div>
          <div className="consultant-users__profile-content">
            <div className="consultant-users__profile-info">
              <h2 className="consultant-users__profile-name">{userData.name}</h2>
              <p className="consultant-users__profile-degree">Role: {userData.role}</p>
              <div className="consultant-users__profile-contact">
                <span className="consultant-users__profile-contact-item">
                  <span className="material-symbols-outlined consultant-users__profile-contact-icon">mail</span>
                  {userData.email}
                </span>
                <span className="consultant-users__profile-contact-separator"></span>
                <span className="consultant-users__profile-contact-item">
                  <span className="material-symbols-outlined consultant-users__profile-contact-icon">badge</span>
                  ID: {userData.userId}
                </span>
                {userData.phone && (
                  <>
                    <span className="consultant-users__profile-contact-separator"></span>
                    <span className="consultant-users__profile-contact-item">
                      <span className="material-symbols-outlined consultant-users__profile-contact-icon">phone</span>
                      {userData.phone}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="consultant-users__profile-separator"></div>
            <div className="consultant-users__profile-stats">
              <div className="consultant-users__stat-item">
                <p className="consultant-users__stat-label">Well-being Score</p>
                <div className="consultant-users__stat-value">
                  <span className="consultant-users__stat-number">{sowbData?.overall?.percent0to100 || '-'}</span>
                  <span className="consultant-users__stat-unit">/100</span>
                </div>
                <span className={`consultant-users__stat-change consultant-users__stat-change--positive`}>
                  <span className="material-symbols-outlined consultant-users__stat-change-icon">trending_up</span>
                  Overall SOWB Score
                </span>
              </div>
              <div className="consultant-users__stat-item">
                <p className="consultant-users__stat-label">Risk Level</p>
                <span className={`consultant-users__risk-badge ${sowbData?.overall?.overallLevel === 'Needs Support' ? 'consultant-users__risk-badge--high' :
                  sowbData?.overall?.overallLevel === 'Taking Steps' ? 'consultant-users__risk-badge--medium' :
                  sowbData?.overall?.overallLevel === 'Feeling Empowered' ? 'consultant-users__risk-badge--low' :
                  'consultant-users__risk-badge--optimal'}`}>
                  {sowbData?.overall?.overallLevel || 'Not Assessed'}
                </span>
                <span className="consultant-users__stat-note">Current status</span>
              </div>
              <div className="consultant-users__stat-item">
                <p className="consultant-users__stat-label">Assessment</p>
                <span className="consultant-users__assessment-type">SOWB10</span>
                <span className="consultant-users__stat-note">
                  Last update: {sowbData?.overall?.createdAt ? formatDate(sowbData.overall.createdAt) : 'Never'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="consultant-users__main-content">
          {/* Left Column - Attribute Scores and Recommendations */}
          <div className="consultant-users__left-column">
            {/* Detailed Attribute Scores */}
            <div className="consultant-users__attribute-scores">
              <div className="consultant-users__section-header">
                <h3 className="consultant-users__section-title">Detailed Attribute Scores</h3>
                <button className="consultant-users__section-link">View Full Analysis</button>
              </div>
              {sowbAccessError && (
                <div className="consultant-users__sowb-access-error">
                  <span className="material-symbols-outlined consultant-users__sowb-access-icon">info</span>
                  <p className="consultant-users__sowb-access-message">{sowbAccessError}</p>
                </div>
              )}
              <div className="consultant-users__attributes-grid">
                {(() => {
                  const hasAssessment = sowbData?.overall != null || (sowbData?.history?.length ?? 0) > 0 || (sowbData?.attributes?.length ?? 0) > 0;
                  if (!hasAssessment || !sowbData?.attributes?.length) {
                    return (
                      <div className="consultant-users__attribute-card consultant-users__attribute-card--empty">
                        <span>No assessment data available</span>
                      </div>
                    );
                  }
                  return sowbData.attributes.map((attribute) => (
                    <div key={attribute.key} className="consultant-users__attribute-card">
                      <div className="consultant-users__attribute-header">
                        <div className={`consultant-users__attribute-icon consultant-users__attribute-icon--${attribute.key.toLowerCase()}`}>
                          <span className="material-symbols-outlined">{attribute.icon}</span>
                        </div>
                        <span className="consultant-users__attribute-name">{attribute.label}</span>
                        <span className="consultant-users__attribute-percentage">
                          {attribute.score === 0 ? '—' : `${attribute.percent}%`}
                        </span>
                      </div>
                      <div className="consultant-users__attribute-progress">
                        <progress
                          className={`consultant-users__attribute-progress-bar consultant-users__attribute-progress-bar--${attribute.key.toLowerCase()}`}
                          value={attribute.score === 0 ? 0 : attribute.percent}
                          max="100"
                        ></progress>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Right Column - Assign groups (consultant only on enterprise) + Assessment History */}
          <div className="consultant-users__right-column">
            {isEnterpriseUserPage && isConsultant && (
              <div className="consultant-users__assign-groups-row">
                <button
                  type="button"
                  className="consultant-users__assign-groups-btn"
                  onClick={() => setShowAssignGroupModal(true)}
                >
                  Assign groups
                </button>
              </div>
            )}
            <div className="consultant-users__assessment-history">
              <div className="consultant-users__section-header">
                <h3 className="consultant-users__section-title">Assessment History</h3>
                <div className="consultant-users__section-actions">
                  <button
                    type="button"
                    className="consultant-users__refresh-button"
                    onClick={() => fetchSOWBData(true)}
                    disabled={sowbRefreshing || !id}
                    aria-label="Refresh assessment history"
                  >
                    <span className={`material-symbols-outlined consultant-users__refresh-icon ${sowbRefreshing ? 'consultant-users__refresh-icon--spinning' : ''}`}>
                      refresh
                    </span>
                    {sowbRefreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <button className="consultant-users__filter-button">
                    <span className="material-symbols-outlined consultant-users__filter-icon">filter_list</span>
                  </button>
                </div>
              </div>
              {sowbAccessError ? (
                <div className="consultant-users__sowb-access-error consultant-users__sowb-access-error--history">
                  <span className="material-symbols-outlined consultant-users__sowb-access-icon">info</span>
                  <p className="consultant-users__sowb-access-message">{sowbAccessError}</p>
                </div>
              ) : (
              <>
              <div className="consultant-users__timeline">
                {sowbData?.history && sowbData.history.length > 0 ? (() => {
                  const displayedHistory = sowbData.history.slice(0, ASSESSMENT_HISTORY_MAX_VISIBLE);
                  return displayedHistory.map((item, index) => (
                    <div key={item.id} className="consultant-users__timeline-item">
                      <div className="consultant-users__timeline-marker">
                        <div className={`consultant-users__timeline-dot ${index === 0 ? 'consultant-users__timeline-dot--current' : 'consultant-users__timeline-dot--past'}`}></div>
                        {index < displayedHistory.length - 1 && (
                          <div className="consultant-users__timeline-line"></div>
                        )}
                      </div>
                      <div className="consultant-users__timeline-content">
                        <div className="consultant-users__timeline-header">
                          <span className={`consultant-users__timeline-label ${index === 0 ? 'consultant-users__timeline-label--current' : ''}`}>
                            {index === 0 ? 'Latest' : formatDate(item.createdAt)}
                          </span>
                          {index === 0 && (
                            <span className="consultant-users__timeline-date">
                              {formatDate(item.createdAt)}
                            </span>
                          )}
                        </div>
                        <div className="consultant-users__timeline-score">
                          <span className="consultant-users__timeline-score-number">
                            {item.overallScore == null || item.overallScore === 0 || !Number.isFinite(item.overallScore)
                              ? '—'
                              : Math.round((item.overallScore / 40) * 100)}
                          </span>
                          <span className={`consultant-users__timeline-score-change consultant-users__timeline-score-change--positive`}>
                            {item.overallLevel}
                          </span>
                        </div>
                        <p className="consultant-users__timeline-notes">
                          SOWB10 Assessment completed
                        </p>
                        <button className="consultant-users__timeline-report-btn">View Report</button>
                      </div>
                    </div>
                  ));
                })() : (
                  <div className="consultant-users__timeline-item">
                    <div className="consultant-users__timeline-marker">
                      <div className="consultant-users__timeline-dot consultant-users__timeline-dot--join"></div>
                    </div>
                    <span className="consultant-users__timeline-join-text">
                      {sowbData === null ? 'Loading assessment history...' : 'No assessments yet'}
                    </span>
                  </div>
                )}
              </div>
              {sowbData?.history && sowbData.history.length > ASSESSMENT_HISTORY_MAX_VISIBLE && (
                <button
                  type="button"
                  className="consultant-users__show-all-btn"
                  onClick={() => setShowAllHistoryModal(true)}
                >
                  Show all
                </button>
              )}
              </>
              )}
            </div>
          </div>
        </div>

      </div>

      <Modal
        isOpen={showAllHistoryModal}
        title="Assessment History"
        onClose={() => setShowAllHistoryModal(false)}
        className="consultant-users__history-modal"
      >
        <div className={`consultant-users ${isDarkMode ? 'consultant-users--dark' : ''}`}>
          <div className="consultant-users__timeline consultant-users__timeline--modal">
            {sowbData?.history?.map((item, index) => (
              <div key={item.id} className="consultant-users__timeline-item">
                <div className="consultant-users__timeline-marker">
                  <div className={`consultant-users__timeline-dot ${index === 0 ? 'consultant-users__timeline-dot--current' : 'consultant-users__timeline-dot--past'}`}></div>
                  {index < (sowbData.history?.length ?? 0) - 1 && (
                    <div className="consultant-users__timeline-line"></div>
                  )}
                </div>
                <div className="consultant-users__timeline-content">
                  <div className="consultant-users__timeline-header">
                    <span className={`consultant-users__timeline-label ${index === 0 ? 'consultant-users__timeline-label--current' : ''}`}>
                      {index === 0 ? 'Latest' : formatDate(item.createdAt)}
                    </span>
                    {index === 0 && (
                      <span className="consultant-users__timeline-date">
                        {formatDate(item.createdAt)}
                      </span>
                    )}
                  </div>
                  <div className="consultant-users__timeline-score">
                    <span className="consultant-users__timeline-score-number">
                      {item.overallScore == null || item.overallScore === 0 || !Number.isFinite(item.overallScore)
                        ? '—'
                        : Math.round((item.overallScore / 40) * 100)}
                    </span>
                    <span className="consultant-users__timeline-score-change consultant-users__timeline-score-change--positive">
                      {item.overallLevel}
                    </span>
                  </div>
                  <p className="consultant-users__timeline-notes">
                    SOWB10 Assessment completed
                  </p>
                  <button className="consultant-users__timeline-report-btn" type="button">View Report</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <AssignGroupModal
        isOpen={showAssignGroupModal}
        onClose={() => setShowAssignGroupModal(false)}
        userId={id}
        consultantDisplayName={userData?.name}
        consultantMeta={id ? `ID: ${userData?.userId ?? id} • ${userData?.role ?? 'Consultant'}` : undefined}
      />
    </div>
  );
};

export default ConsultantUsersPage;
